/**
 * Water-weight engine (pure functions, no I/O). Blueprint phase 4.
 *
 * Ported from the user's live TrueScale model (Methodology entries 7 and 16),
 * so the scale numbers in The Mighty Peanut and TrueScale agree.
 *
 * Idea: a morning weigh-in = true (dry) weight + short-term water + noise.
 * We estimate the water part from what was eaten over the last three days,
 * subtract it from each weigh-in, and smooth what is left (Holt smoothing,
 * which follows a steady loss without lagging behind it). That smoothed line
 * is "true weight". It works with or without a scale: without one, you still
 * get "how much water you are probably carrying today" from food alone.
 *
 * 1. GLYCOGEN WATER (carbs)
 *    Each gram of stored glycogen holds about 3 g of water
 *    (Olsson & Saltin 1970; Kreitzman 1992). Stores are finite, so the
 *    effect saturates. TrueScale's form:
 *      water_g = 3·Store · tanh( k · 3 · (C − Cbase) / (3·Store) )
 *      k = 1 when carbs are above baseline, 0.8 below (refilling is faster
 *          than draining: a modelling choice, disclosed).
 *      C = carbs of the last 3 days, weighted 20/30/50 (oldest → newest).
 *      Store = 0.40 × BW × 15 g/kg (muscle) + 100 g (liver)  [grams glycogen]
 *    Near baseline the slope is 3 g water per g of extra carbs; far from it
 *    the curve flattens toward ±3·Store.
 *
 * 2. SODIUM / POTASSIUM WATER
 *    Extracellular fluid holds ~3.2 g sodium per litre, so each retained gram
 *    of sodium brings roughly 300–350 g of water. Potassium pushes the other
 *    way. TrueScale's form:
 *      water_g = 350 × Δ(Na_g − 0.65·K_g), clamped to −2,000…+2,500 g
 *    Heer 2000 found high salt intake *may not* cause fluid retention the way
 *    classic balance theory predicts, so this is the least certain term.
 *
 * 3. MENSTRUAL CYCLE (optional)
 *    g/kg by cycle phase (same table as TrueScale). These are estimates.
 *
 * Everything is RELATIVE to the user's own baseline (average of the logged
 * days supplied), so a normal day reads ≈ 0.
 */
import { dateFromDayNumber, dayNumber, linearRegression } from '../trajectory/model';

// ---------------------------------------------------------------------
// Constants (all disclosed in the UI)
// ---------------------------------------------------------------------
export const WATER_PER_GLYCOGEN_G = 3;
export const MUSCLE_FRACTION_OF_BW = 0.40;
export const MUSCLE_GLYCOGEN_G_PER_KG = 15;
export const LIVER_GLYCOGEN_G = 100;
export const K_REFILL = 1.0;
export const K_DEPLETE = 0.8;
/** Weights for [3 days ago, 2 days ago, yesterday]. */
export const CARB_DAY_WEIGHTS: readonly [number, number, number] = [0.2, 0.3, 0.5];
export const SODIUM_WATER_G_PER_G = 350;
export const POTASSIUM_OFFSET = 0.65;
export const SODIUM_WATER_MIN_G = -2000;
export const SODIUM_WATER_MAX_G = 2500;
/** Tomorrow's prediction band: ±0.4 lb, widened if your own scatter is bigger. */
export const PREDICTION_MIN_BAND_KG = 0.4 * 0.45359237;
export const DEFAULT_BODY_WEIGHT_KG = 75;
export const MIN_BASELINE_DAYS = 7;
export const DEFAULT_EMA_ALPHA = 0.1;
/** How quickly the smoother's trend (slope) adapts. */
export const DEFAULT_TREND_BETA = 0.05;

export const WATER_CITATIONS = [
  { key: 'olsson1970', text: 'Olsson KE, Saltin B. Variation in total body water with muscle glycogen changes in man. Acta Physiol Scand. 1970;80(1):11-18.', pmid: '5475323' },
  { key: 'kreitzman1992', text: 'Kreitzman SN, Coxon AY, Szaz KF. Glycogen storage: illusions of easy weight loss, excessive weight regain, and distortions in estimates of body composition. Am J Clin Nutr. 1992;56(1 Suppl):292S-293S.', pmid: '1615908' },
  { key: 'heer2000', text: 'Heer M, Baisch F, Kropp J, Gerzer R, Drummer C. High dietary sodium chloride consumption may not induce body fluid retention in humans. Am J Physiol Renal Physiol. 2000;278(4):F585-F595.', pmid: '10751219' },
] as const;

// ---------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------
export interface WaterDayInput {
  /** YYYY-MM-DD (local calendar day). */
  date: string;
  /** False (or mealCount 0) = food not logged that day, so it is skipped, not treated as zero. */
  logged: boolean;
  carbsG?: number | null;
  sodiumMg?: number | null;
  potassiumMg?: number | null;
  /** First (morning) weigh-in of the day, kg. */
  weightKg?: number | null;
}

export interface CycleInput {
  lastPeriodStart: string;
  cycleLengthDays: number;
}

export interface WaterInput {
  days: WaterDayInput[];
  /** Used to size glycogen stores when there are no weigh-ins (no-scale path). */
  bodyWeightKg?: number | null;
  cycle?: CycleInput | null;
  /** The day whose NEXT morning we predict (usually today). Defaults to the last date supplied. */
  today?: string | null;
  /**
   * Is today's food log finished? A half-logged day would read as a low-carb,
   * low-salt day and drag tomorrow's estimate down, so by default today's food
   * is left out of tomorrow's water until the day is marked complete.
   */
  todayComplete?: boolean;
  emaAlpha?: number;
}

export interface WaterBreakdown {
  /** The morning this applies to. */
  date: string;
  carbsG: number | null;
  sodiumG: number | null;
  cycleG: number | null;
  totalG: number;
  /** How many of the 3 preceding days had food logged (0–3). */
  loggedDays: number;
}

export interface TrueWeightPoint {
  date: string;
  scaleKg: number;
  waterKg: number;
  /** Scale minus water. */
  adjustedKg: number;
  /** Smoothed adjusted weight: the "true weight" line. */
  trueKg: number;
  /** Plain smoothed scale weight, for comparison. */
  rawTrendKg: number;
}

export type WaterStatus = 'ok' | 'insufficient_data';

export interface WaterResult {
  status: WaterStatus;
  baseline: { days: number; carbsG: number | null; osmoticG: number | null };
  store: { bodyWeightKg: number; bodyWeightAssumed: boolean; glycogenG: number; maxWaterG: number };
  /** Water estimate for each morning in the input (plus tomorrow). */
  water: WaterBreakdown[];
  today: WaterBreakdown | null;
  tomorrow: WaterBreakdown | null;
  trueWeight: TrueWeightPoint[];
  /** How much day-to-day scatter the model removes (null until ≥10 weigh-ins). */
  noise: { rawSdKg: number; adjustedSdKg: number; explainedPct: number } | null;
  prediction: { date: string; weightKg: number; lowKg: number; highKg: number; slopeKgPerDay: number } | null;
  warnings: string[];
  assumptions: string[];
  citations: typeof WATER_CITATIONS;
}

// ---------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------
export function glycogenStoreG(bodyWeightKg: number): number {
  return MUSCLE_FRACTION_OF_BW * bodyWeightKg * MUSCLE_GLYCOGEN_G_PER_KG + LIVER_GLYCOGEN_G;
}

/** Glycogen-bound water (g) for weighted carbs C vs baseline. Saturating, asymmetric slope. */
export function carbWaterG(weightedCarbsG: number, baselineCarbsG: number, storeG: number): number {
  const amp = WATER_PER_GLYCOGEN_G * storeG;
  if (amp <= 0) return 0;
  const delta = weightedCarbsG - baselineCarbsG;
  const k = delta >= 0 ? K_REFILL : K_DEPLETE;
  return amp * Math.tanh((k * WATER_PER_GLYCOGEN_G * delta) / amp);
}

/** Osmotic load in grams: Na − 0.65·K. */
export function osmoticLoadG(sodiumMg: number, potassiumMg: number): number {
  return sodiumMg / 1000 - POTASSIUM_OFFSET * (potassiumMg / 1000);
}

export function sodiumWaterG(osmoticG: number, baselineOsmoticG: number): number {
  const w = SODIUM_WATER_G_PER_G * (osmoticG - baselineOsmoticG);
  return Math.min(SODIUM_WATER_MAX_G, Math.max(SODIUM_WATER_MIN_G, w));
}

/** Menstrual fluid, g per kg body weight (TrueScale table; estimates). null = no cycle data. */
export function menstrualGPerKg(date: string, cycle: CycleInput | null | undefined): number | null {
  if (!cycle) return null;
  const len = Math.min(45, Math.max(20, Math.round(cycle.cycleLengthDays)));
  const diff = dayNumber(date) - dayNumber(cycle.lastPeriodStart);
  if (diff < 0) return null;
  const cd = (diff % len) + 1;
  if (cd <= 5) return 6.0;              // menstruation, resolving
  if (cd <= len - 14) return 0.0;       // follicular baseline
  if (cd <= len - 12) return 4.0;       // ovulation window
  if (cd <= len - 6) return 2.0;        // mid-luteal
  return 12.5;                          // late luteal
}

/** Weighted mean over the 3 days before `date`, re-normalised over the days that were logged. */
function weighted3(byDay: Map<number, WaterDayInput>, dn: number, pick: (d: WaterDayInput) => number | null): { value: number | null; logged: number } {
  let sw = 0, sv = 0, logged = 0;
  for (let i = 0; i < 3; i++) {
    const d = byDay.get(dn - 3 + i);
    if (!d || !d.logged) continue;
    const v = pick(d);
    if (v === null || !Number.isFinite(v)) continue;
    const w = CARB_DAY_WEIGHTS[i]!;
    sw += w; sv += w * v; logged++;
  }
  return { value: sw > 0 ? sv / sw : null, logged };
}

const num = (v: number | null | undefined): number | null => (v === null || v === undefined || !Number.isFinite(v) ? null : v);
const mean = (xs: number[]): number => xs.reduce((a, b) => a + b, 0) / xs.length;
function sd(xs: number[]): number {
  if (xs.length < 2) return 0;
  const m = mean(xs);
  return Math.sqrt(xs.reduce((a, x) => a + (x - m) * (x - m), 0) / (xs.length - 1));
}

/**
 * Holt's linear (double exponential) smoothing: a level plus a trend.
 * A plain moving average lags behind a steady loss (it always reads too
 * high while you're losing); tracking the trend removes that lag.
 * Multi-day gaps are stepped through one day at a time.
 */
export function holtSmooth(series: Array<{ dn: number; y: number }>, alpha: number, beta: number = DEFAULT_TREND_BETA): number[] {
  const out: number[] = [];
  if (series.length === 0) return out;
  // Start the trend from a straight-line fit of the first week, not from zero.
  const first = series[0]!;
  const warm = series.filter((p) => p.dn <= first.dn + 7);
  let trend: number = warm.length >= 3 ? (linearRegression(warm.map((p) => ({ x: p.dn, y: p.y })))?.slopeKgPerDay ?? 0) : 0;
  let level: number = first.y;
  let last: number = first.dn;
  out.push(level);
  for (let i = 1; i < series.length; i++) {
    const p = series[i]!;
    const gap: number = Math.max(1, p.dn - last);
    // Carry the forecast across skipped days, then update on this weigh-in.
    for (let g = 1; g < gap; g++) level += trend;
    const forecast: number = level + trend;
    const newLevel: number = forecast + alpha * (p.y - forecast);
    trend = (1 - beta) * trend + beta * (newLevel - level);
    level = newLevel;
    last = p.dn;
    out.push(level);
  }
  return out;
}

// ---------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------
export function computeWater(input: WaterInput): WaterResult {
  const alpha = input.emaAlpha ?? DEFAULT_EMA_ALPHA;
  const warnings: string[] = [];
  const assumptions: string[] = [];
  const byDay = new Map<number, WaterDayInput>();
  for (const d of input.days) byDay.set(dayNumber(d.date), d);
  const dns = [...byDay.keys()].sort((a, b) => a - b);

  const empty = (status: WaterStatus): WaterResult => ({
    status, baseline: { days: 0, carbsG: null, osmoticG: null },
    store: { bodyWeightKg: DEFAULT_BODY_WEIGHT_KG, bodyWeightAssumed: true, glycogenG: glycogenStoreG(DEFAULT_BODY_WEIGHT_KG), maxWaterG: 3 * glycogenStoreG(DEFAULT_BODY_WEIGHT_KG) },
    water: [], today: null, tomorrow: null, trueWeight: [], noise: null, prediction: null,
    warnings, assumptions, citations: WATER_CITATIONS,
  });
  if (dns.length === 0) { warnings.push('No days supplied.'); return empty('insufficient_data'); }

  const todayDn = input.today ? dayNumber(input.today) : dns[dns.length - 1]!;
  const todayRow = byDay.get(todayDn);
  if (todayRow && todayRow.logged && input.todayComplete !== true) {
    byDay.set(todayDn, { ...todayRow, logged: false });
  }

  // Baseline = your own average over the logged days supplied.
  const loggedDays = dns.map((n) => byDay.get(n)!).filter((d) => d.logged);
  const carbVals = loggedDays.map((d) => num(d.carbsG)).filter((v): v is number => v !== null);
  const osmVals = loggedDays.map((d) => (num(d.sodiumMg) !== null ? osmoticLoadG(d.sodiumMg as number, num(d.potassiumMg) ?? 0) : null)).filter((v): v is number => v !== null);
  const haveBaseline = carbVals.length >= MIN_BASELINE_DAYS;
  const baseCarbs = haveBaseline ? mean(carbVals) : null;
  const baseOsm = osmVals.length >= MIN_BASELINE_DAYS ? mean(osmVals) : null;
  if (!haveBaseline) warnings.push(`Log food on at least ${MIN_BASELINE_DAYS} days so the model knows your normal (you have ${carbVals.length}).`);

  // Body weight for the glycogen store: latest weigh-in, else the given estimate, else a default.
  const weighed = dns.map((n) => byDay.get(n)!).filter((d) => num(d.weightKg) !== null);
  let bw: number = DEFAULT_BODY_WEIGHT_KG;
  let bwAssumed = true;
  if (weighed.length > 0) { bw = weighed[weighed.length - 1]!.weightKg as number; bwAssumed = false; }
  else if (num(input.bodyWeightKg) !== null) { bw = input.bodyWeightKg as number; bwAssumed = false; }
  else assumptions.push(`No weight on file, so glycogen stores are sized for ${DEFAULT_BODY_WEIGHT_KG} kg. Add a weight (scale or estimate) to sharpen this.`);
  const store = glycogenStoreG(bw);

  const breakdownFor = (dn: number): WaterBreakdown => {
    const date = dateFromDayNumber(dn);
    const c = weighted3(byDay, dn, (d) => num(d.carbsG));
    const o = weighted3(byDay, dn, (d) => (num(d.sodiumMg) !== null ? osmoticLoadG(d.sodiumMg as number, num(d.potassiumMg) ?? 0) : null));
    const carbs = baseCarbs !== null && c.value !== null ? carbWaterG(c.value, baseCarbs, store) : null;
    const sodium = baseOsm !== null && o.value !== null ? sodiumWaterG(o.value, baseOsm) : null;
    const f = menstrualGPerKg(date, input.cycle);
    const cycleG = f === null ? null : f * bw;
    return { date, carbsG: carbs, sodiumG: sodium, cycleG, totalG: (carbs ?? 0) + (sodium ?? 0) + (cycleG ?? 0), loggedDays: c.logged };
  };

  const firstDn = dns[0]!;
  const water: WaterBreakdown[] = [];
  for (let dn = firstDn; dn <= todayDn + 1; dn++) water.push(breakdownFor(dn));
  const waterByDn = new Map<number, WaterBreakdown>();
  water.forEach((w, i) => waterByDn.set(firstDn + i, w));
  const today = waterByDn.get(todayDn) ?? null;
  const tomorrow = waterByDn.get(todayDn + 1) ?? null;
  if (!todayRow || !todayRow.logged) assumptions.push('Today’s food isn’t logged yet, so tomorrow’s estimate leans on the days before.');
  else if (input.todayComplete !== true) assumptions.push('Today’s food log may not be finished, so tomorrow’s estimate uses the days before today. It updates once the day is done.');

  // True weight: subtract water from each weigh-in, then smooth.
  const series = weighed.map((d) => {
    const dn = dayNumber(d.date);
    const w = waterByDn.get(dn);
    const waterKg = w ? w.totalG / 1000 : 0;
    return { dn, date: d.date, scale: d.weightKg as number, waterKg, adjusted: (d.weightKg as number) - waterKg };
  });
  const trueLine = holtSmooth(series.map((p) => ({ dn: p.dn, y: p.adjusted })), alpha);
  const rawLine = holtSmooth(series.map((p) => ({ dn: p.dn, y: p.scale })), alpha);
  const trueWeight: TrueWeightPoint[] = series.map((p, i) => ({
    date: p.date, scaleKg: p.scale, waterKg: p.waterKg, adjustedKg: p.adjusted, trueKg: trueLine[i]!, rawTrendKg: rawLine[i]!,
  }));

  // Day-to-day scatter around each line (skip the first few points while the smoother warms up).
  let noise: WaterResult['noise'] = null;
  const warm = 3;
  if (series.length >= 10) {
    const rawRes = series.slice(warm).map((p, i) => p.scale - rawLine[i + warm]!);
    const adjRes = series.slice(warm).map((p, i) => p.adjusted - trueLine[i + warm]!);
    const rawSd = sd(rawRes), adjSd = sd(adjRes);
    noise = { rawSdKg: rawSd, adjustedSdKg: adjSd, explainedPct: rawSd > 0 ? Math.max(-100, Math.min(100, (1 - (adjSd * adjSd) / (rawSd * rawSd)) * 100)) : 0 };
  }

  // Tomorrow morning = straight-line fit of the water-adjusted weigh-ins over the last 14 days
  // (a regression has no lag on a steady trend, unlike the smoothed line), plus tomorrow's water.
  let prediction: WaterResult['prediction'] = null;
  if (series.length >= 3 && tomorrow) {
    const last = series[series.length - 1]!;
    let recent = series.filter((p) => p.dn > last.dn - 14);
    if (recent.length < 3) recent = series.slice(-3);
    const fit = linearRegression(recent.map((p) => ({ x: p.dn, y: p.adjusted })));
    const target = todayDn + 1;
    const slope = fit ? fit.slopeKgPerDay : 0;
    const level = fit ? fit.interceptKg + slope * target : trueLine[trueLine.length - 1]!;
    const w = level + tomorrow.totalG / 1000;
    const band = Math.max(PREDICTION_MIN_BAND_KG, fit?.residualSdKg ?? 0, noise?.adjustedSdKg ?? 0);
    prediction = { date: tomorrow.date, weightKg: w, lowKg: w - band, highKg: w + band, slopeKgPerDay: slope };
    if (target - last.dn > 7) warnings.push('Your last weigh-in is over a week old, so tomorrow’s number is rough.');
  }

  assumptions.push(
    'Water is measured against your own average over this period, so a normal day reads about zero.',
    'Glycogen: 3 g water per g, stores = 40% of body weight as muscle × 15 g/kg + 100 g liver; faster refill (k 1.0) than drain (k 0.8) is a modelling choice.',
    'Sodium: 350 g water per g of (sodium − 0.65 × potassium), capped at −2.0 to +2.5 kg. This is the least certain part.',
  );
  if (input.cycle) assumptions.push('Cycle fluid values (0–12.5 g/kg by phase) are estimates.');

  return {
    status: haveBaseline ? 'ok' : 'insufficient_data',
    baseline: { days: carbVals.length, carbsG: baseCarbs, osmoticG: baseOsm },
    store: { bodyWeightKg: bw, bodyWeightAssumed: bwAssumed, glycogenG: store, maxWaterG: WATER_PER_GLYCOGEN_G * store },
    water, today, tomorrow, trueWeight, noise, prediction, warnings, assumptions, citations: WATER_CITATIONS,
  };
}
